'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import {
  Search,
  Menu,
  LayoutGrid,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  ShoppingCart,
  Flame,
  User,
  PlusCircle,
  X,
  Moon,
  Sun,
  Globe,
  LayoutDashboard,
  ShoppingBag,
  Store,
  HelpCircle,
  FileText,
  Facebook,
  Twitter,
  Instagram,
  MapPin,
  Check,
} from 'lucide-react';
import { laravelPaths, buildLaravelApiUrl } from '@/lib/site';
import { getNextProxyBase } from '@/lib/nextProxy';
import { AUTH_STATE_CHANGE_EVENT } from '@/lib/authSync';

/** Bundled wordmark when admin logo is missing or fails to load (`public/static-header-logo.png`). */
const BUNDLED_DEFAULT_LOGO = '/static-header-logo.png';

/** Default when no admin logo: env override, else bundled static wordmark. */
function defaultLogoUrl(): string {
  const fromEnv = process.env.NEXT_PUBLIC_SITE_LOGO_URL?.trim();
  if (fromEnv) return fromEnv;
  return BUNDLED_DEFAULT_LOGO;
}
import { dispatchThemeChanged, getPersistedTheme, persistTheme } from '@/lib/themeSync';
import { ALL_LOCATIONS, LOCATION_HIERARCHY } from '@/lib/locations';
import { ThemedExternalLink } from '@/components/ThemedExternalLink';
import { getCategoryIcon } from '@/utils/categoryIcons';
import type { Category } from '@/lib/api';
import { searchPathWithVerifiedOnly } from '@/lib/searchRoutes';
import { dashboardUrlForRole } from '@/lib/authRedirect';
import { useAuth } from '@/components/AuthContext';
import { signalListingNavigation } from '@/components/ListingNavigationProvider';
import { useRef, useState, useEffect, useMemo, useCallback } from 'react';

interface HeaderProps {
  categories: Category[];
  siteName?: string;
  logo?: string;
}

const LANGUAGES = [
  { code: 'EN', label: 'English' },
  { code: 'SI', label: 'සිංහල' },
  { code: 'TA', label: 'தமிழ்' },
];

export function Header({ categories, siteName = 'BuyMe.lk', logo }: HeaderProps) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const headerRef = useRef<HTMLElement>(null);
  const categoryScrollRef = useRef<HTMLDivElement>(null);
  const locationContainerRef = useRef<HTMLDivElement>(null);
  const [headerHeight, setHeaderHeight] = useState(144);
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [isLocationOpen, setIsLocationOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [locationSearchQuery, setLocationSearchQuery] = useState('');
  const [activeProvince, setActiveProvince] = useState<string | null>(null);
  const [activeDistrict, setActiveDistrict] = useState<string | null>(null);
  const [theme, setTheme] = useState<'light' | 'dark'>('light');
  const [language, setLanguage] = useState('EN');
  const [cartCount, setCartCount] = useState(0);
  const [location, setLocation] = useState('');
  const [dynamicLocations, setDynamicLocations] = useState<string[]>([]);
  const [searchMatchedLocations, setSearchMatchedLocations] = useState<string[]>([]);
  const [user, setUser] = useState<{ name: string; email: string; role_id?: number } | null>(null);
  const { openAuthModal } = useAuth();
  const getDashboardUrl = () => {
    if (!user) return laravelPaths.login();
    return dashboardUrlForRole(user.role_id);
  };
  const uniqueCategories = useMemo(() => {
    const hasVehiclesAndSustainable = (categories ?? []).some(
      (cat) => String(cat.name ?? '').trim().toLowerCase() === 'vehicles & sustainable transport'
    );
    const seen = new Set<string>();
    const deduped: Category[] = [];
    for (const cat of categories ?? []) {
      const name = String(cat.name ?? '').trim();
      // Hide legacy "Vehicle" category when the newer merged category exists.
      if (hasVehiclesAndSustainable && name.toLowerCase() === 'vehicle') continue;
      const key = `${String(cat.slug ?? '').trim().toLowerCase()}|${String(cat.name ?? '').trim().toLowerCase()}`;
      if (!key || seen.has(key)) continue;
      seen.add(key);
      deduped.push(cat);
    }
    return deduped;
  }, [categories]);
  const searchedLocations = useMemo(() => {
    const baseSource = [...ALL_LOCATIONS, ...dynamicLocations];
    const merged = new Map<string, string>();
    [...baseSource, ...searchMatchedLocations].forEach((name) => {
      const trimmed = String(name || '').trim();
      if (!trimmed) return;
      const key = trimmed.toLowerCase();
      if (!merged.has(key)) merged.set(key, trimmed);
    });
    const source = Array.from(merged.values());
    const q = locationSearchQuery.trim().toLowerCase();
    if (!q) return [];
    return source
      .filter((name) => name.toLowerCase().includes(q))
      .sort((a, b) => a.localeCompare(b));
  }, [locationSearchQuery, dynamicLocations, searchMatchedLocations]);

  const currentProvince = useMemo(
    () => (activeProvince ? LOCATION_HIERARCHY.find((p) => p.name === activeProvince) ?? null : null),
    [activeProvince],
  );

  const currentDistrict = useMemo(
    () =>
      currentProvince && activeDistrict
        ? currentProvince.districts.find((d) => d.name === activeDistrict) ?? null
        : null,
    [currentProvince, activeDistrict],
  );

  const locationsPreloadedRef = useRef(false);

  // Load the full location list only when the user opens the picker (avoids a heavy request on every page).
  useEffect(() => {
    if (!isLocationOpen || locationsPreloadedRef.current) return;

    let cancelled = false;
    locationsPreloadedRef.current = true;

    fetch(buildLaravelApiUrl('/locations?limit=5000'), {
      headers: { Accept: 'application/json' },
      credentials: 'include',
      cache: 'no-store',
    })
      .then((res) => (res.ok ? res.json() : { data: [] }))
      .then((json) => {
        if (cancelled) return;
        const rows = Array.isArray(json?.data) ? json.data : [];
        const unique = new Map<string, string>();
        rows.forEach((row: { name?: unknown }) => {
          const name = typeof row?.name === 'string' ? row.name.trim() : '';
          if (!name) return;
          const key = name.toLowerCase();
          if (!unique.has(key)) unique.set(key, name);
        });
        setDynamicLocations(Array.from(unique.values()));
      })
      .catch(() => {
        if (!cancelled) {
          locationsPreloadedRef.current = false;
          setDynamicLocations([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isLocationOpen]);

  // Query backend for the typed term so places not in the preloaded top list still appear (e.g. Welisara).
  useEffect(() => {
    const q = locationSearchQuery.trim();
    if (!isLocationOpen || q.length === 0) {
      setSearchMatchedLocations([]);
      return;
    }
    const timer = window.setTimeout(() => {
      fetch(buildLaravelApiUrl(`/locations?q=${encodeURIComponent(q)}&limit=100`), {
        headers: { Accept: 'application/json' },
        credentials: 'include',
        cache: 'no-store',
      })
        .then((res) => (res.ok ? res.json() : { data: [] }))
        .then((json) => {
          const rows = Array.isArray(json?.data) ? json.data : [];
          const items = rows
            .map((row: { name?: unknown }) => (typeof row?.name === 'string' ? row.name.trim() : ''))
            .filter((name: string) => name.length > 0);
          setSearchMatchedLocations(items);
        })
        .catch(() => setSearchMatchedLocations([]));
    }, 180);

    return () => {
      window.clearTimeout(timer);
    };
  }, [isLocationOpen, locationSearchQuery]);

  // Keep a content spacer in sync with the fixed header height
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;

    const syncHeight = () => {
      const next = Math.ceil(el.getBoundingClientRect().height);
      setHeaderHeight(next);
      document.documentElement.style.setProperty('--site-header-height', `${next}px`);
    };

    syncHeight();
    const ro = new ResizeObserver(syncHeight);
    ro.observe(el);
    window.addEventListener('resize', syncHeight);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', syncHeight);
    };
  }, []);

  // Sync location from URL when on search (or other pages that use location)
  useEffect(() => {
    if (pathname === '/search' || pathname.startsWith('/search/')) {
      setLocation(searchParams.get('location') || '');
    }
  }, [pathname, searchParams]);

  // Close location dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (locationContainerRef.current && !locationContainerRef.current.contains(event.target as Node)) {
        setIsLocationOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchUser = useCallback(() => {
    const nx = getNextProxyBase();
    fetch(`${nx}/auth/user`, { credentials: 'include', cache: 'no-store' })
      .then((res) => (res.ok ? res.json() : { user: null }))
      .then((data) => {
        const nextUser = data?.user ?? null;
        setUser(nextUser);
        if (typeof window !== 'undefined') {
          (window as Window & { __BUYME_AUTH__?: boolean }).__BUYME_AUTH__ = Boolean(nextUser);
        }
      })
      .catch(() => {
        setUser(null);
        if (typeof window !== 'undefined') {
          (window as Window & { __BUYME_AUTH__?: boolean }).__BUYME_AUTH__ = false;
        }
      });
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  useEffect(() => {
    const handleAuthChanged = () => {
      fetchUser();
    };
    const handleVisibilityChanged = () => {
      if (document.visibilityState === 'visible') fetchUser();
    };
    const handlePageShow = () => {
      fetchUser();
    };
    window.addEventListener(AUTH_STATE_CHANGE_EVENT, handleAuthChanged);
    window.addEventListener('focus', handleAuthChanged);
    window.addEventListener('pageshow', handlePageShow);
    document.addEventListener('visibilitychange', handleVisibilityChanged);
    return () => {
      window.removeEventListener(AUTH_STATE_CHANGE_EVENT, handleAuthChanged);
      window.removeEventListener('focus', handleAuthChanged);
      window.removeEventListener('pageshow', handlePageShow);
      document.removeEventListener('visibilitychange', handleVisibilityChanged);
    };
  }, [fetchUser]);

  useEffect(() => {
    router.prefetch('/search');
    router.prefetch('/');
    uniqueCategories.slice(0, 8).forEach((cat) => {
      const slug = encodeURIComponent(cat.slug || String(cat.id));
      router.prefetch(`/search/${slug}`);
    });
  }, [router, uniqueCategories]);

  const refreshCartCount = () => {
    const nx = getNextProxyBase();
    fetch(`${nx}/cart/count`, { credentials: 'include', cache: 'no-store' })
      .then((res) => (res.ok ? res.json() : { count: 0 }))
      .then((data: { count?: number }) => {
        setCartCount(typeof data?.count === 'number' ? data.count : 0);
      })
      .catch(() => setCartCount(0));
  };

  useEffect(() => {
    refreshCartCount();
  }, []);

  useEffect(() => {
    const onCartUpdated = (event: Event) => {
      const delta = (event as CustomEvent<{ delta?: number }>).detail?.delta;
      if (typeof delta === 'number' && Number.isFinite(delta)) {
        setCartCount((prev) => Math.max(0, prev + delta));
      }
      // Reconcile with server in the background to keep count accurate.
      window.setTimeout(refreshCartCount, 120);
    };
    window.addEventListener('cart-updated', onCartUpdated as EventListener);
    return () => window.removeEventListener('cart-updated', onCartUpdated as EventListener);
  }, []);

  // Apply theme to document (single place) so UI and DOM stay in sync
  const applyTheme = (value: 'light' | 'dark') => {
    if (typeof document === 'undefined') return;
    document.documentElement.classList.toggle('dark', value === 'dark');
    document.documentElement.style.colorScheme = value;
    document.body.classList.remove('dark');
    persistTheme(value);
    dispatchThemeChanged(value);
  };

  // On mount: read saved theme or system preference and apply (no second effect to avoid race)
  useEffect(() => {
    const stored = getPersistedTheme();
    const prefersDark = typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches;
    const next = stored === 'light' || stored === 'dark' ? stored : prefersDark ? 'dark' : 'light';
    setTheme(next);
    applyTheme(next);
  }, []);

  const toggleTheme = () => {
    setTheme((t) => {
      const next = t === 'light' ? 'dark' : 'light';
      applyTheme(next);
      return next;
    });
  };

  const scrollCategory = (direction: 'left' | 'right') => {
    if (categoryScrollRef.current) {
      categoryScrollRef.current.scrollBy({
        left: direction === 'left' ? -300 : 300,
        behavior: 'smooth',
      });
    }
  };

  const handleLocationSelect = (value: string) => {
    setLocation(value);
    setIsLocationOpen(false);
    setLocationSearchQuery('');
    if (pathname === '/search' || pathname.startsWith('/search/')) {
      const params = new URLSearchParams(searchParams.toString());
      if (value) params.set('location', value);
      else params.delete('location');
      const query = params.toString();
      router.push(query ? `${pathname}?${query}` : pathname);
    } else {
      router.push(value ? `/search?location=${encodeURIComponent(value)}` : '/search');
    }
    setActiveProvince(null);
    setActiveDistrict(null);
  };

  const listingQueryForCategorySwitch = useMemo(() => {
    if (!(pathname === '/search' || pathname.startsWith('/search/'))) return '';
    const params = new URLSearchParams(searchParams.toString());
    // Category changes should start from first page, but keep user-selected sorting and filters.
    params.delete('page');
    return params.toString();
  }, [pathname, searchParams]);

  const buildCategoryNavHref = (categorySlug?: string) => {
    const base = categorySlug ? `/search/${encodeURIComponent(categorySlug)}` : '/search';
    const withQuery = listingQueryForCategorySwitch ? `${base}?${listingQueryForCategorySwitch}` : base;
    const explicitVerifiedOnly = searchParams.get('verified_only') ?? undefined;
    return searchPathWithVerifiedOnly(withQuery, explicitVerifiedOnly ?? undefined);
  };

  const activeCategorySlug = useMemo(() => {
    if (!pathname.startsWith('/search/')) return null;
    const slug = pathname.slice('/search/'.length).split('/')[0] ?? '';
    const decoded = decodeURIComponent(slug).trim();
    return decoded === '' ? null : decoded.toLowerCase();
  }, [pathname]);

  const isBrowseAllActive = pathname === '/search';

  const handleCategoryNavIntent = useCallback((href: string) => {
    signalListingNavigation(href);
  }, []);

  const isCategoryActive = (cat: Category): boolean => {
    if (!activeCategorySlug) return false;
    const slug = String(cat.slug || cat.id).trim().toLowerCase();
    return slug !== '' && slug === activeCategorySlug;
  };

  const preferredLogo = logo?.trim() || defaultLogoUrl();
  const [logoSrc, setLogoSrc] = useState(preferredLogo);

  useEffect(() => {
    setLogoSrc(logo?.trim() || defaultLogoUrl());
  }, [logo]);

  const onLogoError = () => {
    setLogoSrc((current) => (current === BUNDLED_DEFAULT_LOGO ? current : BUNDLED_DEFAULT_LOGO));
  };

  return (
    <>
      <header
        ref={headerRef}
        className="fixed top-0 left-0 right-0 z-40 bg-white dark:bg-neutral-800 border-b border-neutral-200 dark:border-neutral-700 shadow-sm"
      >
        <div className="container mx-auto px-4 py-2.5">
          <div className="flex items-center justify-between gap-3 md:gap-6">
            <div className="flex items-center gap-3 shrink-0">
              <button
                type="button"
                onClick={() => setIsNavOpen(true)}
                className="lg:hidden p-2 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg transition-colors"
                aria-label="Open menu"
              >
                <Menu size={24} aria-hidden />
              </button>
              <Link
                href="/"
                className="relative flex h-8 md:h-10 w-[100px] md:w-[120px] shrink-0 items-center rounded-lg p-1 -ml-1 group"
              >
                <Image
                  src={logoSrc}
                  alt={siteName}
                  fill
                  className="object-contain object-left"
                  sizes="120px"
                  priority
                  unoptimized
                  onError={onLogoError}
                />
              </Link>
            </div>

            <form action="/search" method="get" className="hidden md:flex flex-1 max-w-2xl relative">
              <div className="w-full bg-neutral-100 dark:bg-neutral-700 hover:bg-neutral-50 dark:hover:bg-neutral-600 border border-transparent hover:border-neutral-300 dark:hover:border-neutral-600 focus-within:bg-white dark:focus-within:bg-neutral-700 focus-within:border-brand-orange rounded-full transition-all duration-200 flex items-center px-4 py-2.5">
                <Search size={18} className="text-neutral-400 dark:text-neutral-300 shrink-0 mr-3" />
                <input
                  type="search"
                  name="q"
                  placeholder="Search for anything..."
                  defaultValue={searchQuery}
                  className="bg-transparent border-none outline-none text-sm text-brand-dark dark:text-neutral-100 w-full placeholder-neutral-400 dark:placeholder-neutral-400"
                />
              </div>
            </form>

            <div className="flex items-center gap-2 md:gap-3 shrink-0">
              <ThemedExternalLink
                href={laravelPaths.cart()}
                className="md:hidden flex items-center justify-center min-w-[44px] min-h-[44px] w-11 h-11 text-brand-orange bg-orange-50 dark:bg-orange-900/30 border border-brand-orange/30 dark:border-orange-700/50 hover:bg-orange-100 dark:hover:bg-orange-900/50 rounded-xl transition-all relative shadow-sm"
                title="Shopping Cart"
                aria-label="Shopping cart"
              >
                <ShoppingCart size={22} strokeWidth={2.25} className="shrink-0" aria-hidden />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-brand-orange text-white text-xs font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1 border-2 border-white dark:border-neutral-800">
                    {cartCount > 99 ? '99+' : cartCount}
                  </span>
                )}
              </ThemedExternalLink>
              {/* Location dropdown – same look as buyme-react */}
              <div className="relative hidden md:flex" ref={locationContainerRef}>
                <button
                  type="button"
                  onClick={() => {
                    const next = !isLocationOpen;
                    setIsLocationOpen(next);
                    if (!next) {
                      setLocationSearchQuery('');
                      setActiveProvince(null);
                      setActiveDistrict(null);
                    }
                    setIsLangOpen(false);
                  }}
                  className={`flex items-center gap-1 px-3 py-2 rounded-lg transition-colors border ${isLocationOpen ? 'border-brand-orange ring-2 ring-brand-orange/10' : 'border-neutral-300 dark:border-neutral-600 hover:border-neutral-400 dark:hover:border-neutral-500'} bg-white dark:bg-neutral-800`}
                  aria-label="Location"
                  aria-expanded={isLocationOpen}
                >
                  <MapPin size={18} className="text-brand-orange shrink-0" aria-hidden />
                  <div className="flex flex-col items-start text-left">
                    <span className="text-[10px] font-bold text-neutral-400 dark:text-neutral-500 leading-none uppercase">Location</span>
                    <span className="text-sm font-bold text-brand-dark dark:text-neutral-100 leading-none truncate max-w-[120px]">
                      {location || 'All locations'}
                    </span>
                  </div>
                  <ChevronDown size={14} className={`ml-2 text-neutral-400 dark:text-neutral-500 transition-transform ${isLocationOpen ? 'rotate-180' : ''}`} aria-hidden />
                </button>
                {isLocationOpen && (
                  <div className="absolute top-full right-0 mt-2 w-80 bg-white dark:bg-neutral-800 rounded-xl shadow-2xl border border-neutral-200 dark:border-neutral-700 z-50 overflow-hidden">
                    <div className="p-2 border-b border-neutral-100 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800">
                      <div className="relative">
                        <Search size={14} className="absolute left-3 top-2.5 text-neutral-400 dark:text-neutral-500" aria-hidden />
                        <input
                          type="text"
                          value={locationSearchQuery}
                          onChange={(event) => setLocationSearchQuery(event.target.value)}
                          placeholder="Search city, district, province..."
                          className="w-full pl-8 pr-3 py-2 rounded-lg border border-neutral-200 dark:border-neutral-600 bg-white dark:bg-neutral-700 text-xs text-brand-dark dark:text-neutral-100 placeholder-neutral-400 dark:placeholder-neutral-500 outline-none focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/10"
                        />
                      </div>
                    </div>
                    <div className="max-h-80 overflow-y-auto py-1">
                      {locationSearchQuery.trim() !== '' ? (
                        <>
                          <button
                            type="button"
                            onClick={() => handleLocationSelect('')}
                            className={`w-full text-left px-4 py-2.5 text-sm hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors ${
                              location === '' ? 'font-bold text-brand-orange' : 'font-semibold text-brand-orange'
                            }`}
                          >
                            All locations
                          </button>
                          {searchedLocations.map((item) => (
                            <button
                              key={item}
                              type="button"
                              onClick={() => handleLocationSelect(item)}
                              className={`w-full text-left px-4 py-2.5 text-sm hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors ${
                                location === item ? 'text-brand-orange font-medium' : 'text-neutral-700 dark:text-neutral-300'
                              }`}
                            >
                              {item}
                            </button>
                          ))}
                          {searchedLocations.length === 0 && (
                            <p className="px-4 py-3 text-xs text-neutral-500 dark:text-neutral-400">
                              No locations found.
                            </p>
                          )}
                        </>
                      ) : currentDistrict ? (
                        <>
                          <button
                            type="button"
                            onClick={() => setActiveDistrict(null)}
                            className="w-full flex items-center gap-2 px-4 py-2 text-xs font-medium text-neutral-500 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors"
                          >
                            <ChevronLeft size={13} aria-hidden />
                            Back to districts
                          </button>
                          <button
                            type="button"
                            onClick={() => handleLocationSelect(currentDistrict.name)}
                            className={`w-full text-left px-4 py-2.5 text-sm font-semibold hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors ${
                              location === currentDistrict.name ? 'text-brand-orange' : 'text-brand-orange'
                            }`}
                          >
                            All in {currentDistrict.name}
                          </button>
                          {currentDistrict.cities.map((city) => (
                            <button
                              key={city}
                              type="button"
                              onClick={() => handleLocationSelect(city)}
                              className={`w-full text-left px-4 py-2.5 text-sm hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors ${
                                location === city ? 'text-brand-orange font-medium' : 'text-neutral-700 dark:text-neutral-300'
                              }`}
                            >
                              {city}
                            </button>
                          ))}
                        </>
                      ) : currentProvince ? (
                        <>
                          <button
                            type="button"
                            onClick={() => {
                              setActiveProvince(null);
                              setActiveDistrict(null);
                            }}
                            className="w-full flex items-center gap-2 px-4 py-2 text-xs font-medium text-neutral-500 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors"
                          >
                            <ChevronLeft size={13} aria-hidden />
                            Back to provinces
                          </button>
                          <button
                            type="button"
                            onClick={() => handleLocationSelect(currentProvince.name)}
                            className={`w-full text-left px-4 py-2.5 text-sm font-semibold hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors ${
                              location === currentProvince.name ? 'text-brand-orange' : 'text-brand-orange'
                            }`}
                          >
                            All in {currentProvince.name}
                          </button>
                          {currentProvince.districts.map((district) => (
                            <button
                              key={district.name}
                              type="button"
                              onClick={() => setActiveDistrict(district.name)}
                              className="w-full flex items-center justify-between px-4 py-2.5 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors"
                            >
                              <span>{district.name}</span>
                              <ChevronRight size={13} className="text-neutral-400 dark:text-neutral-500" aria-hidden />
                            </button>
                          ))}
                        </>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() => handleLocationSelect('')}
                            className="w-full flex items-center justify-between px-4 py-2.5 text-sm font-semibold text-brand-orange hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors"
                          >
                            <span>All of Sri Lanka</span>
                            {location === '' && <Check size={13} className="text-brand-orange" aria-hidden />}
                          </button>
                          {LOCATION_HIERARCHY.map((province) => (
                            <button
                              key={province.name}
                              type="button"
                              onClick={() => {
                                setActiveProvince(province.name);
                                setActiveDistrict(null);
                              }}
                              className="w-full flex items-center justify-between px-4 py-2.5 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors"
                            >
                              <span>{province.name}</span>
                              <ChevronRight size={13} className="text-neutral-400 dark:text-neutral-500" aria-hidden />
                            </button>
                          ))}
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div className="h-6 w-px bg-neutral-200 dark:bg-neutral-700 hidden md:block mx-1" />
              <button
                type="button"
                onClick={toggleTheme}
                className="hidden md:flex items-center justify-center w-9 h-9 text-neutral-600 dark:text-neutral-300 hover:text-brand-dark dark:hover:text-neutral-100 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-full transition-colors"
                title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {theme === 'dark' ? <Sun size={20} strokeWidth={1.5} aria-hidden /> : <Moon size={20} strokeWidth={1.5} aria-hidden />}
              </button>
              <div className="h-6 w-px bg-neutral-200 dark:bg-neutral-700 hidden md:block mx-1" />
              <ThemedExternalLink
                href={laravelPaths.cart()}
                className="hidden md:flex items-center justify-center w-10 h-10 text-neutral-600 dark:text-neutral-300 hover:text-brand-orange hover:bg-orange-50 dark:hover:bg-orange-900/20 rounded-lg transition-all relative"
                title="Shopping Cart"
                aria-label="Shopping cart"
              >
                <ShoppingCart size={20} aria-hidden />
                {cartCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-brand-orange text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                    {cartCount > 99 ? '99+' : cartCount}
                  </span>
                )}
              </ThemedExternalLink>
              {/* TEMP: Bargains Forum hidden on frontend
              <ThemedExternalLink
                href={laravelPaths.forum()}
                className="hidden md:flex items-center gap-2 bg-brand-green hover:bg-green-600 text-white px-4 py-2 rounded-lg font-bold text-sm shadow-sm transition-colors"
              >
                <Flame size={18} className="fill-white/20" aria-hidden />
                <span className="hidden md:inline">Bargains Forum</span>
              </ThemedExternalLink>
              */}
              <div className="relative hidden md:block">
                <button
                  type="button"
                  onClick={() => { setIsLangOpen(!isLangOpen); setIsLocationOpen(false); }}
                  className="flex items-center justify-center w-9 h-9 text-neutral-600 dark:text-neutral-300 hover:text-brand-dark dark:hover:text-neutral-100 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-full transition-colors"
                  aria-label="Language"
                  aria-expanded={isLangOpen}
                >
                  <Globe size={20} strokeWidth={1.5} aria-hidden />
                </button>
                {isLangOpen && (
                  <div className="absolute top-full right-0 mt-2 w-32 bg-white dark:bg-neutral-800 rounded-xl shadow-xl border border-neutral-200 dark:border-neutral-700 z-50 py-1 overflow-hidden">
                    {LANGUAGES.map((lang) => (
                      <button
                        key={lang.code}
                        type="button"
                        onClick={() => { setLanguage(lang.code); setIsLangOpen(false); }}
                        className={`w-full text-left px-4 py-2.5 text-sm hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors ${language === lang.code ? 'text-brand-orange font-bold' : 'text-neutral-700 dark:text-neutral-300'}`}
                      >
                        {lang.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {
                <>
                  {user ? (
                    <>
                      <ThemedExternalLink
                        href={getDashboardUrl()}
                        className="hidden md:flex items-center gap-2 text-sm font-bold text-neutral-600 dark:text-neutral-300 hover:text-brand-orange px-3 py-2 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors"
                      >
                        <LayoutDashboard size={16} aria-hidden />
                        Dashboard
                      </ThemedExternalLink>
                      <ThemedExternalLink
                        href={laravelPaths.postAd()}
                        className="hidden md:flex items-center gap-2 bg-brand-dark text-white px-5 py-2 rounded-lg font-bold text-sm hover:bg-neutral-800 transition-all ml-2"
                      >
                        <PlusCircle size={18} aria-hidden />
                        Post Ad
                      </ThemedExternalLink>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => openAuthModal('login')}
                        className="bg-brand-orange text-white px-3 md:px-4 py-2 rounded-lg font-bold text-sm hover:bg-brand-orange-hover transition-all shadow-sm"
                      >
                        Log in
                      </button>
                      <button
                        type="button"
                        onClick={() => openAuthModal('login')}
                        className="hidden md:flex items-center gap-2 bg-brand-dark text-white px-5 py-2 rounded-lg font-bold text-sm hover:bg-neutral-800 transition-all ml-2"
                      >
                        <PlusCircle size={18} aria-hidden />
                        Post Ad
                      </button>
                    </>
                  )}
                </>
              }
            </div>
          </div>

          {/* Mobile Search */}
          <div className="mt-3 md:hidden">
            <form action="/search" method="get" className="relative w-full">
              <Search size={16} className="absolute left-3.5 top-3 text-neutral-400 dark:text-neutral-300 pointer-events-none" />
              <input
                type="search"
                name="q"
                placeholder="Search..."
                className="w-full pl-10 pr-4 py-2.5 bg-neutral-100 dark:bg-neutral-700 border border-transparent rounded-xl text-sm outline-none focus:bg-white dark:focus:bg-neutral-700 focus:border-brand-orange focus:ring-1 focus:ring-brand-orange/20 text-brand-dark dark:text-neutral-100 placeholder-neutral-400 dark:placeholder-neutral-400"
              />
            </form>
          </div>
        </div>

        {/* Category Nav - match React: only Browse All + categories, no separate Categories link */}
        <div className="border-t border-neutral-100 dark:border-neutral-700 bg-white dark:bg-neutral-800">
          <div className="container mx-auto px-4 relative">
            <button
              type="button"
              onClick={() => scrollCategory('left')}
              className="absolute left-1 md:left-2 top-1/2 -translate-y-1/2 z-20 flex items-center justify-center bg-white/95 dark:bg-neutral-800/95 backdrop-blur border border-neutral-200 dark:border-neutral-600 rounded-full p-1.5 md:p-2 shadow-md hover:bg-white dark:hover:bg-neutral-800 active:scale-95 text-neutral-600 dark:text-neutral-300 touch-manipulation"
              aria-label="Scroll categories left"
            >
              <ChevronLeft size={16} aria-hidden />
            </button>
            <button
              type="button"
              onClick={() => scrollCategory('right')}
              className="absolute right-1 md:right-2 top-1/2 -translate-y-1/2 z-20 flex items-center justify-center bg-white/95 dark:bg-neutral-800/95 backdrop-blur border border-neutral-200 dark:border-neutral-600 rounded-full p-1.5 md:p-2 shadow-md hover:bg-white dark:hover:bg-neutral-800 active:scale-95 text-neutral-600 dark:text-neutral-300 touch-manipulation"
              aria-label="Scroll categories right"
            >
              <ChevronRight size={16} aria-hidden />
            </button>
            <div
              ref={categoryScrollRef}
              className="flex items-start justify-start gap-4 md:gap-6 overflow-x-auto no-scrollbar py-3 md:py-4 scroll-smooth pl-11 pr-11 md:pl-12 md:pr-12"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              <Link
                href={buildCategoryNavHref()}
                onPointerDown={(e) => {
                  if (e.button === 0) handleCategoryNavIntent(buildCategoryNavHref());
                }}
                className={`flex flex-col items-center gap-1.5 min-w-[60px] md:min-w-[70px] shrink-0 group/item transition-opacity ${
                  isBrowseAllActive ? 'opacity-100' : 'opacity-70 hover:opacity-100'
                }`}
              >
                <div
                  className={`w-10 h-10 md:w-12 md:h-12 rounded-2xl flex items-center justify-center transition-all duration-300 ${
                    isBrowseAllActive
                      ? 'bg-brand-orange/10 text-brand-orange'
                      : 'bg-neutral-50 dark:bg-neutral-700 text-brand-dark group-hover/item:bg-brand-orange/10 group-hover/item:text-brand-orange'
                  }`}
                >
                  <LayoutGrid size={20} className="md:w-6 md:h-6" aria-hidden />
                </div>
                <span
                  className={`text-[10px] md:text-[11px] font-bold text-center ${
                    isBrowseAllActive
                      ? 'text-brand-orange'
                      : 'text-neutral-500 dark:text-neutral-400 group-hover/item:text-brand-orange'
                  }`}
                >
                  Browse All
                </span>
              </Link>
              {uniqueCategories.map((cat) => {
                const categoryHref = buildCategoryNavHref(cat.slug || String(cat.id));
                return (
                <Link
                  key={cat.id}
                  href={categoryHref}
                  onPointerDown={(e) => {
                    if (e.button === 0) handleCategoryNavIntent(categoryHref);
                  }}
                  className={`flex flex-col items-center gap-1.5 min-w-[60px] md:min-w-[70px] shrink-0 group/item transition-opacity ${
                    isCategoryActive(cat) ? 'opacity-100' : 'opacity-70 hover:opacity-100'
                  }`}
                >
                  <div
                    className={`w-10 h-10 md:w-12 md:h-12 rounded-2xl flex items-center justify-center text-xl md:text-2xl transition-all duration-300 ${
                      isCategoryActive(cat)
                        ? 'bg-brand-orange/10'
                        : 'bg-neutral-50 dark:bg-neutral-700 group-hover/item:bg-brand-orange/10'
                    }`}
                  >
                    {getCategoryIcon(String(cat.id), cat.name, cat.icon ?? undefined)}
                  </div>
                  <span
                    className={`text-[10px] md:text-[11px] font-bold text-center truncate w-full ${
                      isCategoryActive(cat)
                        ? 'text-brand-orange'
                        : 'text-neutral-500 dark:text-neutral-400 group-hover/item:text-brand-orange'
                    }`}
                  >
                    {cat.name}
                  </span>
                </Link>
              );
              })}
            </div>
          </div>
        </div>
      </header>
      <div style={{ height: headerHeight }} aria-hidden className="shrink-0" />

      {/* Mobile Nav Menu - match React Layout: welcome block, then links, then footer */}
      {isNavOpen && (
        <div className="fixed inset-0 z-50 flex justify-start lg:hidden">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
            onClick={() => setIsNavOpen(false)}
            aria-hidden
          />
          <div className="relative w-[85%] max-w-sm bg-white dark:bg-neutral-800 h-full shadow-2xl flex flex-col">
            <div className="p-5 border-b border-neutral-100 dark:border-neutral-700 flex items-center justify-between bg-neutral-50 dark:bg-neutral-800">
              <div className="relative h-8 w-[100px] shrink-0">
                <Image
                  src={logoSrc}
                  alt={siteName}
                  fill
                  className="object-contain object-left"
                  sizes="100px"
                  priority
                  unoptimized
                  onError={onLogoError}
                />
              </div>
              <button
                type="button"
                onClick={() => setIsNavOpen(false)}
                className="p-2 bg-white dark:bg-neutral-700 rounded-full shadow-sm text-neutral-500 hover:text-brand-dark dark:hover:text-neutral-100"
                aria-label="Close menu"
              >
                <X size={20} aria-hidden />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              <div className="p-5 border-b border-neutral-100 dark:border-neutral-700">
                <h3 className="text-lg font-bold text-brand-dark dark:text-neutral-100 mb-1">
                  Welcome{user ? `, ${user.name}` : ', Guest'}
                </h3>
                <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-4">
                  {user ? 'Manage your account and post ads.' : 'Sign in to post ads and manage your account.'}
                </p>
                {user ? (
                  <ThemedExternalLink
                    href={getDashboardUrl()}
                    onClick={() => setIsNavOpen(false)}
                    className="w-full py-2.5 rounded-lg bg-brand-orange text-white font-bold text-sm hover:bg-brand-orange-hover flex items-center justify-center gap-2"
                  >
                    <LayoutDashboard size={16} /> Dashboard
                  </ThemedExternalLink>
                ) : (
                  <button
                    type="button"
                    onClick={() => { setIsNavOpen(false); openAuthModal('login'); }}
                    className="w-full py-2.5 rounded-lg bg-brand-orange text-white font-bold text-sm hover:bg-brand-orange-hover flex items-center justify-center gap-2"
                  >
                    Log in
                  </button>
                )}
              </div>
              <nav className="p-2 space-y-1">
                <Link href="/" onClick={() => setIsNavOpen(false)} className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700 rounded-lg">
                  <LayoutGrid size={18} className="text-neutral-400" aria-hidden /> Home
                </Link>
                <Link href="/search" onClick={() => setIsNavOpen(false)} className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700 rounded-lg">
                  <ShoppingBag size={18} className="text-neutral-400" aria-hidden /> Browse All Ads
                </Link>
                <button
                  type="button"
                  onClick={toggleTheme}
                  className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700 rounded-lg text-left"
                  aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                >
                  {theme === 'dark' ? (
                    <Sun size={18} className="text-neutral-400 shrink-0" aria-hidden />
                  ) : (
                    <Moon size={18} className="text-neutral-400 shrink-0" aria-hidden />
                  )}
                  {theme === 'dark' ? 'Light mode' : 'Dark mode'}
                </button>
                {/* TEMP: Bargains Forum hidden on frontend
                <ThemedExternalLink href={laravelPaths.forum()} className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700 rounded-lg">
                  <Flame size={18} className="text-neutral-400" aria-hidden /> Bargains Forum
                </ThemedExternalLink>
                */}
                <ThemedExternalLink href={laravelPaths.verifiedStores()} className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700 rounded-lg">
                  <Store size={18} className="text-neutral-400" aria-hidden /> Official Stores
                </ThemedExternalLink>
                <div className="h-px bg-neutral-100 dark:bg-neutral-700 my-2 mx-4" />
                <ThemedExternalLink href={laravelPaths.help()} onClick={() => setIsNavOpen(false)} className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700 rounded-lg">
                  <HelpCircle size={18} className="text-neutral-400" aria-hidden /> Help &amp; Support
                </ThemedExternalLink>
                <ThemedExternalLink href={laravelPaths.terms()} onClick={() => setIsNavOpen(false)} className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700 rounded-lg">
                  <FileText size={18} className="text-neutral-400" aria-hidden /> Terms &amp; Privacy
                </ThemedExternalLink>
              </nav>
            </div>
            <div className="p-4 border-t border-neutral-100 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800">
              <div className="flex items-center justify-center gap-4 text-neutral-400">
                <a href="https://www.facebook.com/buyme.lk" target="_blank" rel="noopener noreferrer" className="hover:text-brand-orange transition-colors" aria-label="Facebook">
                  <Facebook size={20} aria-hidden />
                </a>
                <a href="https://www.instagram.com/buyme.lk" target="_blank" rel="noopener noreferrer" className="hover:text-brand-orange transition-colors" aria-label="Instagram">
                  <Instagram size={20} aria-hidden />
                </a>
                <a href="https://www.twitter.com/buyme_lk" target="_blank" rel="noopener noreferrer" className="hover:text-brand-orange transition-colors" aria-label="Twitter">
                  <Twitter size={20} aria-hidden />
                </a>
              </div>
              <p className="text-[10px] text-center text-neutral-400 dark:text-neutral-500 mt-3">v1.2.0 &copy; BuyMe.lk</p>
            </div>
          </div>
        </div>
      )}

    </>
  );
}
